import { type InteractionType } from "@crowdcoursing/api/openapi";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  TextField as MuiTextField,
} from "@mui/material";
import { DateTime } from "luxon";
import { Field, Form } from "react-final-form";

import { TextField } from "~ui/atoms/Form/TextField/TextField";
import { INTERACTION_TYPES } from "~utils/constants/interactionTypes";

const tAddInteraction = "Add Interaction";
const tCancel = "Cancel";
const tAdd = "Add";
const tSubject = "Subject";
const tNotes = "Notes";
const tType = "Type";

interface AddInteractionModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    type: InteractionType;
    subject: string;
    notes: string;
    contactId: string | null;
    occurredAt: string;
  }) => Promise<void>;
}

export const AddInteractionModal = ({ open, onClose, onSubmit }: AddInteractionModalProps) => {
  const handleSubmit = async (values: {
    type: InteractionType;
    subject: string;
    notes: string;
    contactId: string | null;
    occurredAt: string;
  }) => {
    // Ensure we're using today's date with the selected time
    const today = DateTime.now().startOf("day");
    const selectedTime = DateTime.fromFormat(values.occurredAt, "yyyy-MM-dd'T'HH:mm");
    const formattedValues = {
      ...values,
      occurredAt: today
        .set({
          hour: selectedTime.hour,
          minute: selectedTime.minute,
        })
        .toFormat("yyyy-MM-dd'T'HH:mmZZ"),
    };
    await onSubmit(formattedValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Form
        onSubmit={handleSubmit}
        initialValues={{
          contactId: null,
          occurredAt: DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm"),
          type: 1 as InteractionType,
        }}
        render={({ handleSubmit, submitting, hasValidationErrors, errors }) => (
          <form onSubmit={handleSubmit}>
            <DialogTitle>{tAddInteraction}</DialogTitle>
            <DialogContent>
              <Box display="grid" gap={2} py={1}>
                {/* Type and Time on the same row */}
                <Box display="flex" gap={2}>
                  {/* Type */}
                  <Box flex={1}>
                    <Field name="type">
                      {({ input }) => (
                        <FormControl fullWidth>
                          <InputLabel id="type-label">{tType}</InputLabel>
                          <MuiSelect {...input} labelId="type-label" label={tType}>
                            {INTERACTION_TYPES.filter((type) => type.value !== 99).map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box component="span" sx={{ color: type.color }}>
                                    {type.icon}
                                  </Box>
                                  {type.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                      )}
                    </Field>
                  </Box>

                  {/* Time */}
                  <Box flex={1}>
                    <Field name="occurredAt">
                      {({ input }) => {
                        // Always use today's date
                        const today = DateTime.now().startOf("day");
                        const currentValue = input.value
                          ? DateTime.fromFormat(input.value, "yyyy-MM-dd'T'HH:mm")
                          : DateTime.now();
                        // Extract just the time from the current value
                        const timeOnly = currentValue.toFormat("HH:mm");

                        return (
                          <Box>
                            <MuiTextField
                              value={timeOnly}
                              onChange={(e) => {
                                // Parse the time input and combine with today's date
                                const [hours, minutes] = e.target.value.split(":").map(Number);
                                const newDateTime = today
                                  .set({ hour: hours, minute: minutes })
                                  .toFormat("yyyy-MM-dd'T'HH:mm");
                                input.onChange(newDateTime);
                              }}
                              required
                              fullWidth
                              label="Time"
                              type="time"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 60, // 1 minute steps
                              }}
                            />
                          </Box>
                        );
                      }}
                    </Field>
                  </Box>
                </Box>

                <TextField
                  name="subject"
                  required
                  validate={(value) => (!value ? "Required" : undefined)}
                  textFieldProps={{
                    label: tSubject,
                  }}
                />
                <TextField
                  name="notes"
                  textFieldProps={{
                    label: tNotes,
                    multiline: true,
                    rows: 4,
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>{tCancel}</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || hasValidationErrors || Object.keys(errors || {}).length > 0}
              >
                {tAdd}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};
